/**
 * The browser side of the `assist` function (contracts/assist-function.md):
 * - scan (T123, User Story 8): shrink the photo, ask for a `scan`, and turn the answer into a
 *   draft of the purchase form that fills only empty fields, with confident values only;
 * - complete (User Story 9, as changed by the owner on 2026-10-03): send what the form knows of
 *   the wine, and fill its empty fields with the confident values the web search found;
 * - rating: a wine's online ratings ("Note en ligne" on the wine's page).
 * Nothing is saved until the person confirms the form as usual.
 */
import type { TypeDeVin } from '../domain/cellar';
import { isRegionOf } from '../domain/regions';
import type { WineIdentityFormValue } from '../components/WineIdentityFields';

export interface Field<T> {
  value: T;
  confidence: number;
}

export interface ScanFields {
  domaine: Field<string> | null;
  cepage: Field<string> | null;
  parcelle: Field<string> | null;
  annee: Field<number> | null;
  pays: Field<string> | null;
  /** One of the country's regions (owner, 2026-10-03); an older function leaves it out. */
  region?: Field<string> | null;
  typeDeVin: Field<TypeDeVin> | null;
}

/** Which free allowance of Gemini ran out, when the function knows (owner, 2026-10-03). */
export type QuotaPeriod = 'day' | 'minute';

/** Gemini's free allowance ran out: said as such rather than "unavailable". */
export interface QuotaFailure {
  ok: false;
  reason: 'quota';
  period?: QuotaPeriod;
}

export type ScanResult =
  | { ok: true; fields: ScanFields }
  | { ok: false; reason: 'unreadable' | 'unavailable' }
  | QuotaFailure;

export interface Source {
  title: string;
  url: string;
}

/** What the web says of a wine's empty fields (owner, 2026-10-03), each with a confidence. */
export interface CompletionFields {
  pays?: Field<string> | null;
  region?: Field<string> | null;
  cepage?: Field<string> | null;
  typeDeVin?: Field<TypeDeVin> | null;
  drinkUntilYear?: Field<number> | null;
}

export type CompleteResult =
  | {
      ok: true;
      fields: CompletionFields;
      ratings: OnlineRating[];
      searchSuggestions?: string;
    }
  | { ok: false }
  | QuotaFailure;

/** What the form knows of the wine (its text values). */
export interface CompleteRequest {
  domaine: string;
  cepage: string;
  parcelle: string;
  annee: string;
  pays: string;
  typeDeVin: string;
}

/** One site's rating, on its own scale ("4.3 / 5", "93 / 100"); `count` when the site says. */
export interface OnlineRating {
  site: string;
  score: number;
  scale: number;
  count?: number;
}

export type RatingResult =
  | { ok: true; ratings: OnlineRating[]; sources: Source[]; searchSuggestions?: string }
  | { ok: false }
  | QuotaFailure;

/** The wine to look up (the form's or the wine's text values). */
export interface RatingRequest {
  domaine: string;
  cepage: string;
  parcelle: string;
  annee: string;
  typeDeVin: string;
}

export interface AssistClient {
  /** One or two photos of the bottle, front first (owner request, 2026-10-08). */
  scan(imagesBase64: string[]): Promise<ScanResult>;
  /** The web completion of a wine's empty fields, with its ratings (owner, 2026-10-03). */
  complete(request: CompleteRequest): Promise<CompleteResult>;
  /** Online ratings (owner request, 2026-10-03), only when the person asks. */
  rating(request: RatingRequest): Promise<RatingResult>;
}

/** Sends one request to the function and returns its JSON answer (Lambda invoke or a fake). */
export type AssistTransport = (request: Record<string, unknown>) => Promise<unknown>;

/** Below this, a recognised value is left out (owner, 2026-10-03). */
export const LOW_CONFIDENCE = 0.6;
/** The photo's longest side once shrunk (contracts/assist-function.md). */
export const MAX_PHOTO_SIDE = 1500;

const FIELD_NAMES = ['domaine', 'cepage', 'parcelle', 'annee', 'pays', 'typeDeVin'] as const;
/** The fields a scan fills, the country before its region. */
const FILLED = ['domaine', 'cepage', 'parcelle', 'annee', 'pays', 'region', 'typeDeVin'] as const;

/** The function's "quota" answer, cleaned; undefined for anything else. */
function quotaOf(value: unknown): QuotaFailure | undefined {
  const v = value as { ok?: unknown; reason?: unknown; period?: unknown } | null;
  if (typeof v !== 'object' || v === null || v.ok !== false || v.reason !== 'quota') {
    return undefined;
  }
  return {
    ok: false,
    reason: 'quota',
    ...(v.period === 'day' || v.period === 'minute' ? { period: v.period } : {}),
  };
}

function isScanResult(value: unknown): value is ScanResult {
  const v = value as { ok?: unknown; fields?: unknown; reason?: unknown } | null;
  if (typeof v !== 'object' || v === null) return false;
  if (v.ok === false) return v.reason === 'unreadable' || v.reason === 'unavailable';
  if (v.ok !== true || typeof v.fields !== 'object' || v.fields === null) return false;
  const fields = v.fields as Record<string, unknown>;
  return FIELD_NAMES.every((name) => name in fields);
}

const isText = (value: unknown) => typeof value === 'string' && value !== '';

const isSource = (s: { title?: unknown; url?: unknown } | null) =>
  typeof s === 'object' &&
  s !== null &&
  typeof s.title === 'string' &&
  typeof s.url === 'string' &&
  s.url.startsWith('https://');

function isOnlineRating(value: unknown): value is OnlineRating {
  const r = value as Partial<Record<keyof OnlineRating, unknown>> | null;
  return (
    typeof r === 'object' &&
    r !== null &&
    isText(r.site) &&
    typeof r.score === 'number' &&
    typeof r.scale === 'number' &&
    r.score >= 0 &&
    r.score <= r.scale &&
    (r.count === undefined || (Number.isInteger(r.count) && (r.count as number) > 0))
  );
}

function ratingResult(value: unknown): RatingResult {
  const v = value as {
    ok?: unknown;
    ratings?: unknown;
    sources?: unknown;
    searchSuggestions?: unknown;
  } | null;
  if (
    typeof v !== 'object' ||
    v === null ||
    v.ok !== true ||
    !Array.isArray(v.ratings) ||
    !Array.isArray(v.sources)
  ) {
    return { ok: false };
  }
  const sources = (v.sources as ({ title?: unknown; url?: unknown } | null)[]).filter(
    isSource,
  ) as Source[];
  const ratings = sources.length > 0 ? v.ratings.filter(isOnlineRating) : [];
  return {
    ok: true,
    ratings,
    sources: ratings.length > 0 ? sources : [],
    ...(ratings.length > 0 && typeof v.searchSuggestions === 'string' && v.searchSuggestions !== ''
      ? { searchSuggestions: v.searchSuggestions }
      : {}),
  };
}

const COMPLETION_CHECKS: Record<keyof CompletionFields, (value: unknown) => boolean> = {
  pays: isText,
  region: isText,
  cepage: isText,
  typeDeVin: (value) =>
    value === 'rouge' || value === 'blanc' || value === 'rose' || value === 'vendanges_tardives',
  drinkUntilYear: (value) => Number.isInteger(value),
};

/** A field as the function gave it, or null when it is not well formed. */
function checkedField(raw: unknown, check: (value: unknown) => boolean) {
  const f = raw as { value?: unknown; confidence?: unknown } | null;
  return typeof f === 'object' &&
    f !== null &&
    check(f.value) &&
    typeof f.confidence === 'number' &&
    f.confidence >= 0 &&
    f.confidence <= 1
    ? { value: f.value, confidence: f.confidence }
    : null;
}

function completeResult(value: unknown): CompleteResult {
  const v = value as {
    ok?: unknown;
    fields?: unknown;
    ratings?: unknown;
    searchSuggestions?: unknown;
  } | null;
  if (
    typeof v !== 'object' ||
    v === null ||
    v.ok !== true ||
    typeof v.fields !== 'object' ||
    v.fields === null ||
    !Array.isArray(v.ratings)
  ) {
    return { ok: false };
  }
  const raw = v.fields as Record<string, unknown>;
  const fields = Object.fromEntries(
    Object.entries(COMPLETION_CHECKS)
      .filter(([name]) => name in raw)
      .map(([name, check]) => [name, checkedField(raw[name], check)]),
  ) as CompletionFields;
  return {
    ok: true,
    fields,
    ratings: v.ratings.filter(isOnlineRating),
    ...(typeof v.searchSuggestions === 'string' && v.searchSuggestions !== ''
      ? { searchSuggestions: v.searchSuggestions }
      : {}),
  };
}

export function createAssistClient(transport: AssistTransport): AssistClient {
  return {
    async rating({ domaine, cepage, parcelle, annee, typeDeVin }) {
      try {
        const answer = await transport({
          op: 'rating',
          domaine: domaine.trim(),
          cepage: cepage.trim(),
          ...(parcelle.trim() !== '' ? { parcelle: parcelle.trim() } : {}),
          annee: Number(annee),
          ...(typeDeVin !== '' ? { typeDeVin } : {}),
        });
        return quotaOf(answer) ?? ratingResult(answer);
      } catch {
        return { ok: false };
      }
    },
    async complete({ domaine, cepage, parcelle, annee, pays, typeDeVin }) {
      const year = Number(annee);
      const known = (name: string, value: string) =>
        value.trim() !== '' ? { [name]: value.trim() } : {};
      try {
        const answer = await transport({
          op: 'complete',
          domaine: domaine.trim(),
          ...known('cepage', cepage),
          ...known('parcelle', parcelle),
          ...(annee.trim() !== '' && Number.isInteger(year) ? { annee: year } : {}),
          ...known('pays', pays),
          ...known('typeDeVin', typeDeVin),
        });
        return quotaOf(answer) ?? completeResult(answer);
      } catch {
        return { ok: false };
      }
    },
    async scan(imagesBase64) {
      try {
        const answer = await transport({ op: 'scan', images: imagesBase64 });
        return (
          quotaOf(answer) ?? (isScanResult(answer) ? answer : { ok: false, reason: 'unavailable' })
        );
      } catch {
        return { ok: false, reason: 'unavailable' };
      }
    },
  };
}

/** The size to shrink a photo to: longest side 1,500px at most, never enlarged. */
export function shrunkSize(width: number, height: number, max = MAX_PHOTO_SIDE) {
  const scale = Math.min(1, max / Math.max(width, height));
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

/** The photo as a base64 JPEG, shrunk in the browser before it is sent (Safari and Chrome). */
export async function shrinkPhoto(file: Blob): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const { width, height } = shrunkSize(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
  return dataUrl.slice(dataUrl.indexOf(',') + 1);
}

/**
 * The draft: each recognised value fills its field only if the person left it empty and its
 * confidence is at least LOW_CONFIDENCE; anything less is left out (owner, 2026-10-03). The region
 * fills only when it is one of the country's in the draft.
 */
export function applyScan(
  identity: WineIdentityFormValue,
  fields: ScanFields,
): WineIdentityFormValue {
  const next = { ...identity };
  for (const name of FILLED) {
    const found = fields[name];
    if (!found || next[name] !== '' || found.confidence < LOW_CONFIDENCE) continue;
    const value = String(found.value);
    if (name === 'region' && !isRegionOf(next.pays, value)) continue;
    (next as Record<(typeof FILLED)[number], string>)[name] = value;
  }
  return next;
}

/**
 * The form completed from the web (owner, 2026-10-03): each empty field takes the value found when
 * its confidence is at least LOW_CONFIDENCE, the region only if it is one of the country's, and an
 * unset drink-by year the last year to drink. `filled` says whether anything changed.
 */
export function applyCompletion(
  identity: WineIdentityFormValue,
  drinkUntilYear: string,
  fields: CompletionFields,
): { identity: WineIdentityFormValue; drinkUntilYear: string; filled: boolean } {
  const next = { ...identity };
  let filled = false;
  for (const name of ['pays', 'region', 'cepage', 'typeDeVin'] as const) {
    const found = fields[name];
    if (!found || next[name] !== '' || found.confidence < LOW_CONFIDENCE) continue;
    if (name === 'region' && !isRegionOf(next.pays, found.value)) continue;
    (next as Record<typeof name, string>)[name] = found.value;
    filled = true;
  }
  const until = fields.drinkUntilYear;
  const takeYear = until && until.confidence >= LOW_CONFIDENCE && drinkUntilYear === '';
  if (takeYear) filled = true;
  return {
    identity: next,
    drinkUntilYear: takeYear ? String(until.value) : drinkUntilYear,
    filled,
  };
}
